import { Fragment, useMemo, useState } from "react";
import type { TFunction } from "i18next";
import type { DefaultTheme } from "styled-components";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { Table } from "../../../../components/Table/Table";
import { BarentzAPIErrorMessage } from "../../../../components/BarentzAPIErrorMessage/BarentzAPIErrorMessage";
import { Chip } from "../../../../components/Chips/Chips";
import { CaretDownIcon } from "../../../../components/Icons/Icons";
import { LogTimeline } from "../../../../components/Logs/LogTimeline";
import {
  getBarentzApiStatusConfig,
  StatusBadge,
} from "../../../../components/Status/Status";
import { formatDateTimeWithTime } from "../../../../util/util";
import { ActivityHistoryItem, APIPushSkuState } from "./types";
import {
  CaretWrapper,
  DateTime,
  DiffText,
  FlexDiv,
  GroupTitle,
  ModifiedText,
  SubItemContainer,
  SubItemContent,
  SubItemHeader,
} from "./styles";

const EMPTY_VALUE = "-";
const NOT_PUSHED = "not_pushed";

const TABLE_PROPS = {
  isLoading: false,
  error: undefined,
  enableDefaultSort: false,
  enableColumnResize: false,
  lastChildleftAlign: true as const,
  cellVerticalAlignTop: true,
};

type APIPushFieldKey = "sku_id" | "api_status" | "target_system" | "error";
type CellSide = "before" | "after";

interface APIPushSkuRow {
  attribute: string;
  fieldKey: APIPushFieldKey;
  before: string;
  after: string;
  beforeApiStatus: string;
  afterApiStatus: string;
}

interface SkuPair {
  skuId: string;
  before?: APIPushSkuState;
  after?: APIPushSkuState;
}

type FieldDef = { label: string; key: APIPushFieldKey; fallback: string };

export const getAPIPushSkuFields = (t: (key: string) => string): FieldDef[] => [
  { label: t("SKU ID"), key: "sku_id", fallback: EMPTY_VALUE },
  { label: t("API Status"), key: "api_status", fallback: EMPTY_VALUE },
  { label: t("Target System"), key: "target_system", fallback: EMPTY_VALUE },
  { label: t("Error"), key: "error", fallback: EMPTY_VALUE },
];

function getValue(
  sku: APIPushSkuState | undefined,
  key: APIPushFieldKey,
  fallback: string
): string {
  const val = sku?.[key];
  if (val === undefined || val === null || val === "") return fallback;
  return String(val);
}

function buildSkuPairs(item: ActivityHistoryItem): SkuPair[] {
  const map = new Map<string, SkuPair>();
  const touch = (sku: APIPushSkuState, part: "before" | "after") => {
    const cur = map.get(sku.sku_id);
    map.set(sku.sku_id, {
      skuId: sku.sku_id,
      before: part === "before" ? sku : cur?.before,
      after: part === "after" ? sku : cur?.after,
    });
  };
  (item.state_before?.skus ?? []).forEach((s) => touch(s, "before"));
  (item.state_after?.skus ?? []).forEach((s) => touch(s, "after"));
  return [...map.values()];
}

function filterRowsForPair(pair: SkuPair, fieldRows: FieldDef[]): FieldDef[] {
  return fieldRows.filter(({ key }) => {
    if (key !== "error") return true;
    const beforeErr = getValue(pair.before, "error", EMPTY_VALUE);
    const afterErr = getValue(pair.after, "error", EMPTY_VALUE);
    return !(beforeErr === EMPTY_VALUE && afterErr === EMPTY_VALUE);
  });
}

function TargetSystemChip({
  targetSystem,
  syncStatus,
  theme,
}: {
  targetSystem: string;
  syncStatus: string;
  theme: DefaultTheme;
}) {
  if (targetSystem === EMPTY_VALUE) {
    return <span>{targetSystem}</span>;
  }
  return (
    <Chip
      name={targetSystem}
      data-chip={targetSystem}
      active={false}
      onClick={() => {}}
      style={{
        backgroundColor:
          syncStatus === "success"
            ? theme.resolve("color_bg_surface_success_diff")
            : theme.resolve("color_bg_surface_error_diff"),
      }}
    >
      {targetSystem}
    </Chip>
  );
}

function renderAPIPushValueCell(
  row: APIPushSkuRow,
  side: CellSide,
  theme: DefaultTheme,
  t: TFunction
) {
  const value = side === "before" ? row.before : row.after;
  const other = side === "before" ? row.after : row.before;
  const syncStatus =
    side === "before" ? row.beforeApiStatus : row.afterApiStatus;

  switch (row.fieldKey) {
    case "target_system":
      return (
        <TargetSystemChip
          targetSystem={value}
          syncStatus={syncStatus}
          theme={theme}
        />
      );
    case "api_status": {
      const normalized = value === EMPTY_VALUE ? NOT_PUSHED : value;
      const { color, text } = getBarentzApiStatusConfig(normalized, t);
      return <StatusBadge color={color} text={text} />;
    }
    case "error":
      return (
        <div
          style={{
            whiteSpace: "normal",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          <BarentzAPIErrorMessage raw={value} />
        </div>
      );
    default: {
      const hasChange =
        side === "before"
          ? value !== other && value !== EMPTY_VALUE
          : value !== other && other !== EMPTY_VALUE;
      if (value === EMPTY_VALUE) return <span>{value}</span>;
      return side === "before" ? (
        <DiffText removed={hasChange}>{value}</DiffText>
      ) : (
        <DiffText added={hasChange}>{value}</DiffText>
      );
    }
  }
}

function buildRowData(
  pair: SkuPair,
  fieldRows: FieldDef[],
  skuDisplayName: string,
  skuIdLabel: string
): APIPushSkuRow[] {
  return fieldRows.map(({ label, key, fallback }) => ({
    attribute: label === skuIdLabel ? skuDisplayName : label,
    fieldKey: key,
    before: getValue(pair.before, key, fallback),
    after: getValue(pair.after, key, fallback),
    beforeApiStatus: getValue(pair.before, "api_status", EMPTY_VALUE),
    afterApiStatus: getValue(pair.after, "api_status", EMPTY_VALUE),
  }));
}

export function APIPushItem({ item }: { item: ActivityHistoryItem }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const fieldRows = useMemo(() => getAPIPushSkuFields(t), [t]);
  const skuPairs = useMemo(() => buildSkuPairs(item), [item]);
  const skuIdLabel = t("SKU ID");
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [expandedSkus, setExpandedSkus] = useState<Record<string, boolean>>({});

  const targetSystems = Array.isArray(item.activity_args?.target_systems)
    ? item.activity_args?.target_systems.join(", ")
    : EMPTY_VALUE;

  const columns = useMemo<ColumnDef<APIPushSkuRow>[]>(
    () => [
      { header: t("Name"), accessorKey: "attribute" },
      {
        header: t("Before"),
        accessorKey: "before",
        cell: ({ row }) =>
          renderAPIPushValueCell(row.original, "before", theme, t),
      },
      {
        header: t("After"),
        accessorKey: "after",
        cell: ({ row }) =>
          renderAPIPushValueCell(row.original, "after", theme, t),
      },
    ],
    [t, theme]
  );

  return (
    <LogTimeline
      id={item.id}
      username={item.created_by || item.last_modified_by}
      date={item.created_at || item.modified_at}
      message={
        <>
          API PUSH to <b>{targetSystems}</b>
        </>
      }
      isExpanded={timelineOpen}
      onToggle={() => setTimelineOpen((v) => !v)}
    >
      <GroupTitle>
        <b>{t("SKUs")}</b>
      </GroupTitle>
      {skuPairs.map((pair) => {
        const skuDisplayName =
          pair.after?.sku_id_display_name ||
          pair.before?.sku_id_display_name ||
          skuIdLabel ||
          "";
        const skuTimestamp =
          pair.after?.activity_timestamp ||
          pair.before?.activity_timestamp ||
          "";
        const open = expandedSkus[pair.skuId] ?? false;
        const rowsForPair = filterRowsForPair(pair, fieldRows);
        const tableData = buildRowData(
          pair,
          rowsForPair,
          skuDisplayName,
          skuIdLabel
        );

        return (
          <Fragment key={`${item.id}-${pair.skuId}`}>
            <SubItemContainer>
              <SubItemHeader
                onClick={() =>
                  setExpandedSkus((prev) => ({
                    ...prev,
                    [pair.skuId]: !prev[pair.skuId],
                  }))
                }
              >
                <CaretWrapper isExpanded={open}>
                  <CaretDownIcon
                    height={16}
                    width={16}
                    fill={theme.secondaryTextColor}
                  />
                </CaretWrapper>
                <FlexDiv>
                  <ModifiedText>
                    <b>{pair.skuId}</b>
                  </ModifiedText>
                  {skuTimestamp ? (
                    <DateTime>{formatDateTimeWithTime(skuTimestamp)}</DateTime>
                  ) : null}
                </FlexDiv>
              </SubItemHeader>
              {open ? (
                <SubItemContent>
                  <Table columns={columns} data={tableData} {...TABLE_PROPS} />
                </SubItemContent>
              ) : null}
            </SubItemContainer>
          </Fragment>
        );
      })}
    </LogTimeline>
  );
}
